import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { MaterialModule } from '../../../../shared/material.module';
import { TranscodeService } from '../../../../services/transcode.service';
import { LanguageService } from '../../../../services/language.service';
import { Transcode } from '../../../../models/transcode.model';
import { CidService } from '../../../../services/cid.service';
import { CIDRequestDTO, DriverDTO, WitnessRequestDTO } from '../../../../models/cid.model';
import { PdfGeneratorService } from '../../../../services/pdf-generator.service';
import { NotificationService } from '../../../../services/notification.service';
import { VehicleUtils } from '../../../../utils/vehicle.utils';
import { ClientConfigurationService } from '../../../../services/client-configuration.service';
import { Driver } from '../../../../services/driver.service';
import {
  buildVehicleDamageFileName,
  getTypeDocIdByDescription,
  TYPE_DOC_CID,
  TYPE_DOC_DANNI_VEICOLO,
  TYPE_DOC_DOCUMENTO_TESTIMONE,
  TYPE_DOC_RIEPILOGO_PDF,
} from '../../../../utils/type-doc.util';

@Component({
  selector: 'app-step-summary',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MaterialModule,
    TranslatePipe,
  ],
  templateUrl: './step-summary.component.html',
  styleUrl: './step-summary.component.scss',
})
export class StepSummaryComponent implements OnInit {
  isSubmitting = false;
  isSubmitted = false;

  @Input() form!: FormGroup;
  @Input() summaryData: any;
  @Input() accidentDynamicsData: any;
  @Input() involvedVehiclesData: any;
  @Input() damageData: any;
  @Input() crashesInfoData: any;

  @Output() goBack = new EventEmitter<void>();
  @Output() submitSinistro = new EventEmitter<void>();
  @Output() submitSuccess = new EventEmitter<any>();
  @Output() goToStep = new EventEmitter<number>();

  private transcodes: Transcode[] = [];
  private typeDocTranscodes: Transcode[] = [];
  private cachedPDFFile: File | null = null;

  constructor(
    private transcodeService: TranscodeService,
    private languageService: LanguageService,
    private cidService: CidService,
    private pdfGenerator: PdfGeneratorService,
    private notificationService: NotificationService,
    private clientConfigurationService: ClientConfigurationService,
  ) {}

  ngOnInit() {
    this.transcodeService.getTranscodes('CID_CIRCUMSTANCE').subscribe((circumstances) => {
      this.transcodes = circumstances;
    });
    this.transcodeService.getTranscodes('TYPE_DOC').subscribe((typeDocs) => {
      this.typeDocTranscodes = typeDocs;
    });
  }

  getFormattedDate(): string {
    if (!this.summaryData?.dataOra) return 'Non disponibile';
    return new Date(this.summaryData.dataOra).toLocaleDateString('it-IT');
  }

  getFormattedTime(): string {
    if (!this.summaryData?.dataOra) return 'Non disponibile';
    return new Date(this.summaryData.dataOra).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }

  hasInjuries(): boolean {
    return this.damageData?.presenzaFeriti === 'true';
  }

  hasMaterialDamage(): boolean {
    return this.damageData?.danniMateriali === 'true';
  }

  hasWitnesses(): boolean {
    return this.damageData?.presenzaTestimoni === 'true';
  }

  getVehicleADynamics(): string[] {
    const dynamics = this.accidentDynamicsData?.accidentDynamics?.vehicleA;
    if (!dynamics || !Array.isArray(dynamics)) return [];
    return dynamics.map((id: string) => {
      const transcode = this.transcodes.find(t => t.id?.toString() === id.toString());
      return transcode?.description || `Dinamica ${id}`;
    });
  }

  getInvolvedVehicles(): any[] {
    const vehicles = this.involvedVehiclesData?.involvedVehicles;
    if (!vehicles || !Array.isArray(vehicles)) return [];
    return vehicles.filter((v: any) => v.plate?.trim());
  }

  getInvolvedVehiclesWithDynamics(): any[] {
    const vehicles = this.getInvolvedVehicles();
    const vehicleLetters = VehicleUtils.getVehicleLetters();
    return vehicles.map((vehicle: any, index: number) => {
      const vehicleLetter = vehicleLetters[index + 1];
      const dynamicsKey = `vehicle${vehicleLetter}`;
      const dynamics = this.accidentDynamicsData?.accidentDynamics?.[dynamicsKey] || [];
      return {
        plate: vehicle.plate,
        letter: vehicleLetter,
        nome: vehicle.nome || '',
        cognome: vehicle.cognome || '',
        indirizzo: vehicle.indirizzo || '',
        telefono: vehicle.telefono || '',
        dynamics: dynamics.map((id: string) => {
          const transcode = this.transcodes.find(t => t.id?.toString() === id.toString());
          return transcode?.description || `Dinamica ${id}`;
        }),
      };
    });
  }

  hasCounterpartyData(vehicle: any): boolean {
    return !!(vehicle?.nome?.trim() || vehicle?.cognome?.trim() || vehicle?.indirizzo?.trim() || vehicle?.telefono?.trim());
  }

  getVehicleLetter(index: number): string {
    return VehicleUtils.getVehicleLetter(index) || index.toString();
  }

  getVehicleCardClass(vehicleIndex: number): string {
    return `vehicle-${this.getVehicleLetter(vehicleIndex).toLowerCase()}`;
  }

  getWitnesses(): any[] {
    const wizardComponent = (window as any).wizardComponent;
    if (!wizardComponent?.damageComponent?.witnesses) return [];
    return wizardComponent.damageComponent.witnesses.filter((w: any) => w.nome || w.cognome);
  }

  getCurrentLocationAddress(): string {
    const wizardComponent = (window as any).wizardComponent;
    return wizardComponent?.currentLocation?.address || this.summaryData?.indirizzo || 'Non specificato';
  }

  private getWizardSelectedDriver(): Driver | null {
    const selectedDriverId = this.summaryData?.selectedDriverId;
    if (!selectedDriverId || selectedDriverId === VehicleUtils.NOT_AVAILABLE) {
      return null;
    }
    const wizardComponent = (window as any).wizardComponent;
    return (
      wizardComponent?.driverVehicleComponent?.availableDrivers?.find(
        (d: Driver) => d.id.toString() === selectedDriverId.toString()
      ) ?? null
    );
  }

  getDriverName(): string {
    const selectedDriverId = this.summaryData?.selectedDriverId;
    const selectedDriver = this.getWizardSelectedDriver();
    if (selectedDriverId && selectedDriverId !== VehicleUtils.NOT_AVAILABLE && selectedDriver) {
      if (this.clientConfigurationService.driverDisplayMode === 'DRIVER_CODE') {
        const code = (selectedDriver.driverCardNumber ?? '').toString().trim();
        if (code) {
          return code;
        }
      }
      return selectedDriver.name || 'Non specificato';
    }
    if (this.crashesInfoData?.driverInfo?.name && !selectedDriverId) return this.crashesInfoData.driverInfo.name;
    return this.summaryData?.nome || 'Non specificato';
  }

  getDriverSurname(): string {
    const selectedDriverId = this.summaryData?.selectedDriverId;
    const selectedDriver = this.getWizardSelectedDriver();
    if (selectedDriverId && selectedDriverId !== VehicleUtils.NOT_AVAILABLE && selectedDriver) {
      if (this.clientConfigurationService.driverDisplayMode === 'DRIVER_CODE') {
        const code = (selectedDriver.driverCardNumber ?? '').toString().trim();
        if (code) {
          const sn = (selectedDriver.surname || '').trim();
          const nm = (selectedDriver.name || '').trim();
          const full = `${sn} ${nm}`.trim();
          return full || 'Non specificato';
        }
      }
      return selectedDriver.surname || 'Non specificato';
    }
    if (this.crashesInfoData?.driverInfo?.surname && !selectedDriverId) return this.crashesInfoData.driverInfo.surname;
    return this.summaryData?.cognome || 'Non specificato';
  }

  getFullDriverName(): string {
    const selectedDriver = this.getWizardSelectedDriver();
    if (selectedDriver) {
      if (this.clientConfigurationService.driverDisplayMode === 'DRIVER_CODE') {
        return this.clientConfigurationService.getDriverSelectLabel(selectedDriver);
      }
      const name = selectedDriver.name || '';
      const surname = selectedDriver.surname || '';
      if (!name && !surname) {
        return 'Non specificato';
      }
      return `${name} ${surname}`.trim();
    }
    const name = this.getDriverName();
    const surname = this.getDriverSurname();
    if (name === 'Non specificato' && surname === 'Non specificato') return 'Non specificato';
    return `${name} ${surname}`.trim();
  }

  getWitnessTitle(index: number): string {
    const witnesses = this.getWitnesses();
    return witnesses.length === 1
      ? this.languageService.translate('witnesses.single')
      : `${this.languageService.translate('witnesses.single')} ${index + 1}`;
  }

  hasWitnessDocument(): boolean {
    return this.getWitnessDocuments().length > 0;
  }

  getWitnessDocuments(): { file: File; url: string; name: string }[] {
    const wizardComponent = (window as any).wizardComponent;
    return wizardComponent?.witnessDocuments ?? [];
  }

  onGoBack(): void {
    this.goToStep.emit(2);
  }

  async onSubmitSinistro(): Promise<void> {
    if (this.isSubmitting || this.isSubmitted) return;
    this.isSubmitting = true;
    const crashData = this.buildCrashPayload();
    const wizardComponent = (window as any).wizardComponent;
    const allFiles: File[] = [];
    const attachmentTypeIds: (number | undefined)[] = [];
    const danniVeicoloTypeId = getTypeDocIdByDescription(
      this.typeDocTranscodes,
      TYPE_DOC_DANNI_VEICOLO
    );
    const documentoTestimoneTypeId = getTypeDocIdByDescription(
      this.typeDocTranscodes,
      TYPE_DOC_DOCUMENTO_TESTIMONE
    );
    const riepilogoPdfTypeId = getTypeDocIdByDescription(
      this.typeDocTranscodes,
      TYPE_DOC_RIEPILOGO_PDF
    );

    const hasVehiclePhotos = wizardComponent?.vehiclePhotos
      ? Object.values(wizardComponent.vehiclePhotos).some(
          (photos: unknown) => Array.isArray(photos) && photos.length > 0
        )
      : false;
    const hasWitnessDocuments = (wizardComponent?.witnessDocuments?.length ?? 0) > 0;

    if (hasVehiclePhotos && !danniVeicoloTypeId) {
      this.notificationService.showApiError('Tipologia DANNI_VEICOLO non disponibile.');
      this.isSubmitting = false;
      return;
    }
    if (hasWitnessDocuments && !documentoTestimoneTypeId) {
      this.notificationService.showApiError('Tipologia DOCUMENTO_TESTIMONE non disponibile.');
      this.isSubmitting = false;
      return;
    }
    if (!riepilogoPdfTypeId) {
      this.notificationService.showApiError('Tipologia RIEPILOGO_PDF non disponibile.');
      this.isSubmitting = false;
      return;
    }

    if (wizardComponent?.vehiclePhotos) {
      Object.entries(wizardComponent.vehiclePhotos).forEach(([vehicleLetter, photos]: [string, any]) => {
        if (Array.isArray(photos)) {
          let plate = vehicleLetter === 'A' ? (this.summaryData?.targa || 'VEICOLO_A') : (this.getInvolvedVehiclesWithDynamics().find((v: any) => v.letter === vehicleLetter)?.plate || `VEICOLO_${vehicleLetter}`);
          photos.forEach((p: any) => {
            allFiles.push(new File([p.file], buildVehicleDamageFileName(plate, p.file.name), { type: p.file.type }));
            attachmentTypeIds.push(danniVeicoloTypeId);
          });
        }
      });
    }

    if (wizardComponent?.witnessDocuments?.length) {
      wizardComponent.witnessDocuments.forEach((doc: { file: File }) => {
        allFiles.push(doc.file);
        attachmentTypeIds.push(documentoTestimoneTypeId);
      });
    }

    try {
      this.cachedPDFFile = null;
      const pdfFile = await this.getPDFFile();
      allFiles.push(pdfFile);
      attachmentTypeIds.push(riepilogoPdfTypeId);
    } catch (error) {
      console.error('Errore generazione PDF:', error);
    }

    const vehicleId = wizardComponent?.vehicleId;
    if (!vehicleId) {
      this.notificationService.showApiError('Errore: ID veicolo non disponibile.');
      this.isSubmitting = false;
      return;
    }

    this.cidService.createCIDcomplete(crashData, allFiles, vehicleId, attachmentTypeIds).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.submitSuccess.emit(response);
      },
      error: (error) => {
        this.isSubmitting = false;
        console.error('Errore nel salvataggio del sinistro:', error);
        this.notificationService.showApiError('Si è verificato un errore durante il salvataggio del sinistro.');
      },
    });
  }

  resetSubmitState() {
    this.isSubmitting = false;
    this.isSubmitted = false;
  }

  async downloadPDF(): Promise<void> {
    try {
      this.cachedPDFFile = null;
      const pdfFile = await this.getPDFFile();
      const url = URL.createObjectURL(pdfFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFile.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download PDF:', error);
    }
  }

  private buildCrashPayload(): CIDRequestDTO {
    const crashDate = new Date(this.summaryData?.dataOra);
    const wizardComponent = (window as any).wizardComponent;
    const payload: any = {
      datecrash: crashDate.toISOString(),
      note: this.damageData?.descrizioneDanni || '',
      locationLat: wizardComponent?.currentLocation?.lat?.toString() || '0',
      locationLon: wizardComponent?.currentLocation?.lng?.toString() || '0',
      locationAddress: wizardComponent?.currentLocation?.address || '',
      lat: wizardComponent?.incidentLocation?.lat?.toString() || '0',
      lon: wizardComponent?.incidentLocation?.lng?.toString() || '0',
      address: wizardComponent?.incidentLocation?.address || '',
      vehicleA: this.buildVehicleA(),
      vehicleB: this.buildVehicleBArray(),
      wounded: this.hasInjuries(),
      physicalDamage: this.hasMaterialDamage(),
      isWitnesses: this.hasWitnesses(),
      fromApp: true,
      scannedCid: false,
    };

    if (this.hasWitnesses()) {
      const witnesses = this.buildWitnesses();
      if (witnesses.length > 0) payload.witnesses = witnesses;
    }

    const selectedDriverId = this.summaryData?.selectedDriverId;
    // if (selectedDriverId === VehicleUtils.NOT_AVAILABLE) {
    //   payload.newDriver = { id: null, phoneNumber: this.summaryData?.telefono || null };
    // } else
    if (selectedDriverId && selectedDriverId !== VehicleUtils.NOT_AVAILABLE) {
      payload.newDriver = { id: selectedDriverId, phoneNumber: this.summaryData?.telefono || null };
    } else if (this.crashesInfoData?.driverInfo?.driverId && !selectedDriverId) {
      payload.newDriver = { id: this.crashesInfoData.driverInfo.driverId, phoneNumber: this.summaryData?.telefono || null };
    } else {
      payload.newDriver = { id: null, phoneNumber: this.summaryData?.telefono || null };
    }

    return payload;
  }

  private buildVehicleA(): any {
    const vehicleACircumstances = this.accidentDynamicsData?.accidentDynamics?.vehicleA || [];
    return {
      plate: this.summaryData?.targa || '',
      circumstances: vehicleACircumstances.map((id: string) => parseInt(id)),
    };
  }

  private buildVehicleBArray(): any[] {
    return this.getInvolvedVehiclesWithDynamics().map((vehicle: any) => {
      const payload: any = {
        plate: vehicle.plate,
        circumstances: (this.accidentDynamicsData?.accidentDynamics?.[`vehicle${vehicle.letter}`] || []).map((id: string) => parseInt(id)),
      };
      const driver = this.buildCounterpartyDriver(vehicle);
      if (driver) payload.driver = driver;
      return payload;
    });
  }

  private buildCounterpartyDriver(vehicle: any): DriverDTO | undefined {
    const driver: DriverDTO = {};
    const name = vehicle.nome?.trim();
    const surname = vehicle.cognome?.trim();
    const address = vehicle.indirizzo?.trim();
    const phoneNumber = vehicle.telefono?.trim();

    if (name) driver.name = name;
    if (surname) driver.surname = surname;
    if (address) driver.address = address;
    if (phoneNumber) driver.phoneNumber = phoneNumber;

    return Object.keys(driver).length > 0 ? driver : undefined;
  }

  private buildWitnesses(): WitnessRequestDTO[] {
    const wizardComponent = (window as any).wizardComponent;
    if (!wizardComponent?.damageComponent?.witnesses) return [];
    return wizardComponent.damageComponent.witnesses
      .filter((w: any) => w.nome || w.cognome)
      .map((w: any) => ({
        firstName: w.nome || '',
        lastName: w.cognome || '',
        email: w.email || '',
        phoneNumber: w.telefono || '',
        witnessStatement: w.dichiarazione || '',
      }));
  }

  private async getPDFFile(): Promise<File> {
    if (this.cachedPDFFile) return this.cachedPDFFile;
    this.cachedPDFFile = await this.generatePDFFile();
    return this.cachedPDFFile;
  }

  private async generatePDFFile(): Promise<File> {
    const wizardComponent = (window as any).wizardComponent;
    let allVehiclePhotos: { [key: string]: string[] } = {};
    if (wizardComponent?.vehiclePhotos) {
      Object.entries(wizardComponent.vehiclePhotos).forEach(([letter, photos]: [string, any]) => {
        if (Array.isArray(photos)) allVehiclePhotos[letter] = photos.map((p: any) => p.url);
      });
    }
    const pdfData = {
      formattedDate: this.getFormattedDate(),
      formattedTime: this.getFormattedTime(),
      address: this.getCurrentLocationAddress(),
      hasInjuries: this.hasInjuries(),
      hasMaterialDamage: this.hasMaterialDamage(),
      hasWitnesses: this.hasWitnesses(),
      driverName: this.getFullDriverName(),
      driverEmail: this.summaryData?.email || '',
      driverPhone: this.summaryData?.telefono || '',
      vehicleBrand: this.summaryData?.brand || '',
      vehicleModel: this.summaryData?.modelloBus || '',
      vehiclePlate: this.summaryData?.targa || '',
      accidentDynamics: this.getVehicleADynamics(),
      allVehiclePhotos,
      involvedVehiclesWithDynamics: this.getInvolvedVehiclesWithDynamics(),
      witnesses: this.getWitnesses(),
      witnessDocumentPhotos: (wizardComponent?.witnessDocuments ?? []).map((doc: { url: string }) => doc.url),
    };
    const pdfBlob = await this.pdfGenerator.generateCrashReportAsBlob(pdfData, [], [], []);
    const now = new Date();
    const fileName = `riepilogo_sinistro_${String(now.getDate()).padStart(2, '0')}_${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}.pdf`;
    return new File([pdfBlob], fileName, { type: 'application/pdf' });
  }
}
